import {
  type AddToCartMetadata,
  type CartGiftWithPurchaseAttributeValue,
  type CartProductConnection,
  type CartProductVariant,
} from "../state";
import {
  type ComponentizableCartLine,
  type CartLine,
  type Maybe,
  type AttributeInput,
  type SelectedOption,
} from "@shopify/hydrogen-react/storefront-api-types";
import { type PartialDeep } from "type-fest";
import { parseGid } from "@shopify/hydrogen-react";
import {
  CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS,
  CartLocation,
  EMPTY_ADD_TO_CART_METADATA,
  STOREFRONT_CART_ATTRIBUTES,
} from "@tablescape/constants";
import { TablescapeContext, TablescapeEvent } from "./tablescapeContext";
import { type useCartLine } from "@shopify/hydrogen-react";
// eslint-disable-next-line no-restricted-imports
import {
  formatProduct,
  getMsrpForVariant,
} from "@tablescape/utilities/product";

export const formatCartCollection = (collection: {
  products: {
    nodes: CartProductConnection[];
  };
}) => ({
  products: collection.products.nodes.map((node) => formatProduct(node)),
});

export const hasCartLineAttribute = (
  cartLine:
    | PartialDeep<
        CartLine | ComponentizableCartLine,
        { recurseIntoArrays: true }
      >
    | undefined,
  key: STOREFRONT_CART_ATTRIBUTES,
  valueFn?: (value: Maybe<string> | undefined) => boolean
) =>
  !!cartLine?.attributes?.some((attr) => {
    return attr?.key === key && (valueFn ? valueFn(attr?.value) : true);
  });

export const getCartLineAttributeValue = (
  cartLine:
    | PartialDeep<
        CartLine | ComponentizableCartLine,
        { recurseIntoArrays: true }
      >
    | undefined,
  key: STOREFRONT_CART_ATTRIBUTES
) =>
  cartLine?.attributes?.find((attr) => {
    return attr?.key === key;
  })?.value;

export const findRelatedCartProductGiftWithPurchaseLine = (
  lines:
    | PartialDeep<
        (CartLine | ComponentizableCartLine)[],
        { recurseIntoArrays: true }
      >
    | undefined,
  id: CartLine["id"]
) =>
  lines?.find(
    (giftLine) =>
      giftLine?.id === id &&
      hasCartLineAttribute(
        giftLine,
        STOREFRONT_CART_ATTRIBUTES.GIFT_WITH_PURCHASE
      )
  );

export const findRelatedCartSpendingGiftWithPurchaseLine = (
  lines:
    | PartialDeep<
        (CartLine | ComponentizableCartLine)[],
        { recurseIntoArrays: true }
      >
    | undefined,
  spendingThreshold?: string
) => {
  return lines?.find((line) =>
    hasCartLineAttribute(
      line,
      STOREFRONT_CART_ATTRIBUTES.GIFT_WITH_PURCHASE,
      (value) => {
        if (value) {
          try {
            const parsedValue = JSON.parse(value);
            return spendingThreshold
              ? parsedValue?.cartSpendingGiftThreshold === spendingThreshold
              : parsedValue?.cartSpendingGiftThreshold;
          } catch {
            return false;
          }
        }
        return false;
      }
    )
  );
};

export const getGiftWithPurchaseAttributeValue = (
  cartLine:
    | PartialDeep<
        CartLine | ComponentizableCartLine,
        { recurseIntoArrays: true }
      >
    | undefined,
  key?: keyof CartGiftWithPurchaseAttributeValue
) => {
  const gwpAttribute = cartLine?.attributes?.find(
    (attribute) =>
      attribute?.key === STOREFRONT_CART_ATTRIBUTES.GIFT_WITH_PURCHASE
  );
  try {
    const parsedValue = gwpAttribute?.value
      ? JSON.parse(gwpAttribute.value)
      : undefined;
    return key && parsedValue ? parsedValue[key] : parsedValue;
  } catch {
    return undefined;
  }
};

type CartLineAttributes = PartialDeep<
  (CartLine | ComponentizableCartLine)["attributes"],
  { recurseIntoArrays: true }
>;

/**
 * Derives gift badge text and GWP-specific removability from cart line attributes.
 */
export const deriveGiftBadgeState = (
  attributes: CartLineAttributes | undefined,
  defaultGiftBadgeText: string
): { giftBadgeText: string; isRemovable: boolean } => {
  const line = { attributes };

  const badgeFromAttribute = getGiftWithPurchaseAttributeValue(
    line,
    CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS.GIFT_BADGE
  );

  const giftBadgeText: string = badgeFromAttribute
    ? String(badgeFromAttribute)
    : hasCartLineAttribute(line, STOREFRONT_CART_ATTRIBUTES.GIFT_WITH_PURCHASE)
    ? defaultGiftBadgeText
    : "";

  const isRemovable =
    giftBadgeText === "" ||
    getGiftWithPurchaseAttributeValue(
      line,
      CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS.IS_REMOVABLE
    ) === true;

  return { giftBadgeText, isRemovable };
};

/**
 * Derives the option label and color swatch handle for a gift item.
 * Used by CartGiftWithPurchaseItem to display variant details.
 */
export const deriveGiftItemOptionDisplay = (
  selectedOptions: SelectedOption[],
  isColorOption: (o: SelectedOption) => boolean,
  isDefaultOption: (o: SelectedOption) => boolean,
  handleize: (s: string) => string
): { colorSwatchHandle: string | undefined; optionLabel: string } => {
  const colorOption = selectedOptions.find(isColorOption);
  const colorSwatchHandle = colorOption
    ? handleize(colorOption.value)
    : undefined;

  const optionLabel = selectedOptions
    .filter((o) => !isDefaultOption(o))
    .slice()
    .sort((a, b) => {
      const aIsColor = isColorOption(a);
      const bIsColor = isColorOption(b);
      if (aIsColor === bIsColor) return 0;
      return aIsColor ? -1 : 1;
    })
    .map((o) => o.value)
    .join(" / ");

  return { colorSwatchHandle, optionLabel };
};

/**
 * Builds cart line inputs for one GWP tier. Used by emit and by batched multi-tier emit.
 */
export const buildGiftWithPurchaseLineInputs = (
  gwpAttributeValue: CartGiftWithPurchaseAttributeValue,
  variants: Array<Partial<CartProductVariant> & { id: string }>
) =>
  variants.map((variant) => ({
    attributes: [
      {
        key: STOREFRONT_CART_ATTRIBUTES.GIFT_WITH_PURCHASE,
        value: JSON.stringify(gwpAttributeValue),
      },
      {
        key: STOREFRONT_CART_ATTRIBUTES.MSRP,
        value: getMsrpForVariant(variant),
      },
    ],
    id: parseGid(variant.id).id,
    quantity: 1,
  }));

/**
 * Builds cart line inputs for GWP gift variant(s) from a single eligible tier and emits ADD_TO_CART.
 */
export const emitSingleTierGwpLinesToCart = (
  gwpAttributeValue: CartGiftWithPurchaseAttributeValue,
  variants: Array<Partial<CartProductVariant> & { id: string }>
): void => {
  const lines = buildGiftWithPurchaseLineInputs(gwpAttributeValue, variants);
  TablescapeContext.emit(TablescapeEvent.ADD_TO_CART, {
    cart: {
      lines,
      metadata: {
        ...EMPTY_ADD_TO_CART_METADATA,
        location: CartLocation.GWP_AUTO_ADD,
      },
    },
  });
};

/**
 * Build cart line inputs for multiple GWP tiers and emits ADD_TO_CART. Used when one item satisfies multiple tiers (e.g. high-price item). One cart update adds all.
 */
export const emitMultiTierGwpLinesToCart = (
  tiers: Array<{
    gwpAttributeValue: CartGiftWithPurchaseAttributeValue;
    variants: Array<Partial<CartProductVariant> & { id: string }>;
  }>
): void => {
  const lines = tiers.flatMap(({ gwpAttributeValue, variants }) =>
    buildGiftWithPurchaseLineInputs(gwpAttributeValue, variants)
  );
  if (lines.length === 0) return;
  TablescapeContext.emit(TablescapeEvent.ADD_TO_CART, {
    cart: {
      lines,
      metadata: {
        ...EMPTY_ADD_TO_CART_METADATA,
        location: CartLocation.GWP_AUTO_ADD,
      },
    },
  });
};

export const getRelatedCartLinesToRemove = (
  cartLineId: CartLine["id"],
  selectedGiftsWithPurchase:
    | ReturnType<typeof useCartLine>["selectedGiftsWithPurchase"]
    | undefined
) => {
  let linesToRemove = [cartLineId];

  if (selectedGiftsWithPurchase?.length) {
    linesToRemove = linesToRemove.concat(
      selectedGiftsWithPurchase
        .filter((giftLine): giftLine is CartLine => !!giftLine?.id)
        .map(({ id }) => id)
    );
  }

  return linesToRemove;
};

export const addIntelligemsLineItemAttribute = (
  variantId: string,
  attributes: Maybe<AttributeInput[]>
) => {
  if (!("igData" in window) || !attributes) {
    return attributes;
  }

  const igPrice = window.igData?.price?.getPriceByVariantId(variantId);
  const currencyRate = window.Shopify?.currency?.rate;

  if (!igPrice || !currencyRate) {
    return attributes;
  }

  const intelligemsLineItemAttribute = {
    key: STOREFRONT_CART_ATTRIBUTES.INTELLIGEMS_PRICE,
    value: String(
      Math.round((parseFloat(igPrice) / parseFloat(currencyRate)) * 100)
    ),
  };

  return [...attributes, intelligemsLineItemAttribute];
};

export const hasIntelligemsLineItemAttribute = (
  cartLine:
    | PartialDeep<
        CartLine | ComponentizableCartLine,
        { recurseIntoArrays: true }
      >
    | undefined
) =>
  hasCartLineAttribute(cartLine, STOREFRONT_CART_ATTRIBUTES.INTELLIGEMS_PRICE);

export const hasBundlesLineItemAttribute = (
  cartLine:
    | PartialDeep<
        CartLine | ComponentizableCartLine,
        { recurseIntoArrays: true }
      >
    | undefined
) => hasCartLineAttribute(cartLine, STOREFRONT_CART_ATTRIBUTES.BUNDLES);

export const hasRelatedItemsLineItemAttribute = (
  cartLine:
    | PartialDeep<
        CartLine | ComponentizableCartLine,
        { recurseIntoArrays: true }
      >
    | undefined
) => hasCartLineAttribute(cartLine, STOREFRONT_CART_ATTRIBUTES.RELATED_ITEMS);

/**
 * True while the cart snapshot is in the transient `replace_line` bundle-transform gap:
 * the source line id from cart metadata is no longer present, so Shopify may still be
 * applying the replacement bundle line.
 *
 * Spending-threshold GWP must not react to this snapshot — excluding the transforming
 * line can make spending subtotals (excluding GWP gift lines) dip briefly even when other
 * paid lines keep the real cart above the promo threshold.
 *
 * Pair with clearing `transformMode` / `relatedCartLineId` on the Tablescape cart once
 * the expected bundle line is observed (`TablescapeEvent.RESET_ADD_TO_CART_METADATA` in
 * `useAddToCartManager`) so stale metadata does not keep this stuck true after the
 * transform completes.
 */
export const isCartInReplaceLineBundleTransformGap = ({
  cartMetadata,
  lines,
}: {
  cartMetadata: AddToCartMetadata | null | undefined;
  lines:
    | PartialDeep<
        (CartLine | ComponentizableCartLine)[],
        { recurseIntoArrays: true }
      >
    | null
    | undefined;
}): boolean => {
  if (cartMetadata?.transformMode !== "replace_line") {
    return false;
  }

  const relatedCartLineId = cartMetadata.relatedCartLineId;
  if (!relatedCartLineId) {
    return false;
  }

  const list = lines ?? [];
  if (list.some((line) => line?.id === relatedCartLineId)) {
    return false;
  }

  return true;
};

export const hasTotalWithoutBMSMLineItemAttribute = (
  cartLine:
    | PartialDeep<
        CartLine | ComponentizableCartLine,
        { recurseIntoArrays: true }
      >
    | undefined
) =>
  hasCartLineAttribute(cartLine, STOREFRONT_CART_ATTRIBUTES.TOTAL_WITHOUT_BMSM);

export const getTotalWithoutBMSMValue = (
  cartLine:
    | PartialDeep<
        CartLine | ComponentizableCartLine,
        { recurseIntoArrays: true }
      >
    | undefined
) =>
  parseFloat(
    cartLine?.attributes?.find((attr) => {
      return attr?.key === STOREFRONT_CART_ATTRIBUTES.TOTAL_WITHOUT_BMSM;
    })?.value ?? "0"
  ) / 100;

export const hasGiftWithPurchaseLineItemAttribute = (
  cartLine:
    | PartialDeep<
        CartLine | ComponentizableCartLine,
        { recurseIntoArrays: true }
      >
    | undefined
) =>
  hasCartLineAttribute(cartLine, STOREFRONT_CART_ATTRIBUTES.GIFT_WITH_PURCHASE);
